import { useState, useEffect, FC } from "react";

interface TypewriterProps {
  text: string; // Can contain line breaks
  delay?: number; // Delay between characters
  lineDelay?: number; // Delay after finishing a line
  infinite?: boolean;
  width?: string;
  color?: string; // Text color, cursor will match
}

const Typewriter: FC<TypewriterProps> = ({
  text,
  delay = 100,
  lineDelay = 500,
  infinite = true, // default looping
}) => {
  const lines = text.split("\n"); // split into lines
  const [lineIndex, setLineIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [typedLines, setTypedLines] = useState<string[]>([]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (lineIndex < lines.length) {
      const currentLine = lines[lineIndex];

      if (charIndex <= currentLine.length) {
        timeout = setTimeout(() => {
          setTypedLines((prev) => {
            const newLines = [...prev];
            newLines[lineIndex] = currentLine.slice(0, charIndex);
            return newLines;
          });
          setCharIndex((prev) => prev + 1);
        }, delay);
      } else {
        // Move to next line after lineDelay
        timeout = setTimeout(() => {
          setCharIndex(0);
          setLineIndex((prev) => prev + 1);
        }, lineDelay);
      }
    } else if (infinite) {
      // Restart loop
      timeout = setTimeout(() => {
        setTypedLines([]);
        setLineIndex(0);
        setCharIndex(0);
      }, lineDelay);
    }

    return () => clearTimeout(timeout);
  }, [charIndex, lineIndex, delay, lineDelay, infinite, lines]);

  return (
    <div
      style={{
        display: "inline-block",
        whiteSpace: "pre-wrap",
        overflow: "hidden",
        verticalAlign: "bottom",
      }}
      className="typed-cls"
    >
      {typedLines.map((line, i) => (
        <div key={i}>
          {line}
          {i === typedLines.length - 1 && (
            <span className="typewriter-cursor">|</span>
          )}
        </div>
      ))}
    </div>
  );
};

export default Typewriter;
